import React, { useState } from "react";
import { FaWallet, FaBuilding, FaCoins, FaPlus } from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { useAlert } from "../../context/AlertContext";
import { useGetEndUserWalletQuery, useGrantEndUserOkkaliraMutation } from "../../store";
import { EmptyState, MascotLoadingOverlay, StatCard } from "../shared";
import { formatDate } from "../wallet/walletTxUi";
import { WALLET_TX_UI, formatKurus, formatOkkalira, rejectionLabel } from "../../utils/enduserWallet";
import OkkaliraGrantModal from "./OkkaliraGrantModal";

/** Hareket satırının tutar metni — para kuruş, Okkalira adet */
function txAmount(tx) {
  const ui = WALLET_TX_UI[tx.type] || WALLET_TX_UI.payment;
  if (tx.type === "okkaliraGrant") return `${ui.sign}${formatOkkalira(tx.okkalira)} Okkalira`;
  return `${ui.sign}${formatKurus(tx.amount)}`;
}

function TxRow({ tx }) {
  const ui = WALLET_TX_UI[tx.type] || WALLET_TX_UI.payment;
  const rejected = tx.type === "rejected";
  const extras = rejected
    ? // Ret (#410): neden + makine; bakiye değişmedi
      [rejectionLabel(tx.code), tx.serialNo && `Makine ${tx.serialNo}`]
    : [
        tx.type === "topup" && tx.okkalira > 0 && `+${formatOkkalira(tx.okkalira)} hediye Okkalira`,
        tx.periodicAmount > 0 && `${formatKurus(tx.periodicAmount)} firma kotasından`,
        tx.okkaliraValue > 0 && `${formatKurus(tx.okkaliraValue)} Okkalira ile`,
        tx.type === "payment" && tx.walletAmount > 0 && `${formatKurus(tx.walletAmount)} kişisel bakiyeden`,
        tx.by && `${[tx.by.name, tx.by.lastName].filter(Boolean).join(" ")} yükledi`,
        tx.note,
        tx.status !== "success" && (tx.status === "pending" ? "Bekliyor" : "Başarısız"),
      ];
  const extrasText = extras.filter(Boolean).join(" · ");
  return (
    <li className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 last:border-b-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-SemiBold px-2 py-0.5 rounded-md ${ui.chip}`}>{ui.label}</span>
          <span className="text-[13px] font-Medium text-gray-900 truncate">{tx.description}</span>
        </div>
        {extrasText && <p className="text-xs text-gray-500 mt-1 break-words">{extrasText}</p>}
        {rejected && tx.message && (
          <p className="text-xs text-red-600 mt-0.5 break-words">{tx.message}</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className={`text-[13px] font-SemiBold ${ui.amount}`}>{txAmount(tx)}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(tx.date)}</p>
      </div>
    </li>
  );
}

/**
 * Organizma: son kullanıcı cüzdanı (#397) — bakiyeler, son hareketler,
 * izinliyse Okkalira yükleme. Sözleşme: Workspace docs/api-contracts/enduser-wallet-panel.md
 */
export default function EndUserWalletTab({ userId, userName }) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const { showSuccess, showError } = useAlert();
  const canGrant = hasPermission("users.okkaliraGrant");
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, isError } = useGetEndUserWalletQuery(
    { token: auth?.accessToken, userId },
    { skip: !userId || !auth?.accessToken }
  );
  const [grantOkkalira, { isLoading: granting }] = useGrantEndUserOkkaliraMutation();

  const wallet = data?.data;

  const handleGrant = async ({ amount, note }) => {
    try {
      await grantOkkalira({ token: auth.accessToken, userId, amount, note }).unwrap();
      showSuccess(`${formatOkkalira(amount)} Okkalira yüklendi.`);
      setModalOpen(false);
    } catch (err) {
      showError(err?.data?.message || "Okkalira yüklenemedi.");
    }
  };

  if (isLoading) {
    return (
      <div className="relative">
        <MascotLoadingOverlay message="Cüzdan yükleniyor..." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-2xl bg-gray-100 motion-safe:animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !wallet) {
    return <EmptyState message="Cüzdan bilgisi alınamadı" suggestion="Sayfayı yenileyip tekrar deneyin." />;
  }

  const { balances, employee, transactions = [] } = wallet;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard title="Kişisel Bakiye" count={formatKurus(balances.walletRemaining)} icon={<FaWallet />} color="emerald" />
        <StatCard
          title={employee ? `Firma Kotası · ${employee.firmName || "Firma"}` : "Firma Kotası"}
          count={employee ? formatKurus(balances.periodicWalletRemaining) : "—"}
          icon={<FaBuilding />}
          color="blue"
        />
        <StatCard title="Okkalira" count={formatOkkalira(balances.okkaliraRemaining)} icon={<FaCoins />} color="amber" />
      </div>

      {employee?.templateName && (
        <p className="text-xs text-gray-500 px-1">
          Çalışan şablonu: <span className="font-Medium text-gray-700">{employee.templateName}</span>
        </p>
      )}

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
          <span className="text-sm font-SemiBold text-gray-800">Son Hareketler</span>
          {canGrant && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-SemiBold text-white bg-main rounded-lg hover:bg-mainSecond"
            >
              <FaPlus className="w-3 h-3" />
              Okkalira Yükle
            </button>
          )}
        </div>
        {transactions.length === 0 ? (
          <div className="p-4">
            <EmptyState message="Hareket yok" suggestion="Bu kullanıcının henüz cüzdan hareketi yok." />
          </div>
        ) : (
          <ul className="px-4">
            {transactions.map((tx, i) => (
              <TxRow key={`${tx.type}-${tx.date}-${i}`} tx={tx} />
            ))}
          </ul>
        )}
      </div>

      {canGrant && (
        <OkkaliraGrantModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleGrant}
          saving={granting}
          userName={userName}
        />
      )}
    </div>
  );
}
